import { Link } from 'react-router-dom';

// Builds the internal or external link target based on the card's
// linkType. Returns null when there's nothing to link to.
const getLinkHref = ({ linkType, linkProduct, linkCategory, linkBrand, linkCustomUrl }) => {
    if (linkType === 'product' && linkProduct) {
        return { to: `/product/${linkProduct._id || linkProduct}`, external: false };
    }
    if (linkType === 'category' && linkCategory) {
        const slug = linkCategory.slug || linkCategory;
        return { to: `/products?category=${slug}`, external: false };
    }
    if (linkType === 'brand' && linkBrand) {
        return { to: `/products?brand=${encodeURIComponent(linkBrand)}`, external: false };
    }
    if (linkType === 'custom' && linkCustomUrl) {
        return { to: linkCustomUrl, external: true };
    }
    return null;
};

// Maps a 3x3 grid position to the classes needed to place the button
// there, absolutely, independent of the text content flow above it.
const buttonPositionClasses = {
    'top-left': 'top-6 left-6',
    'top-center': 'top-6 left-1/2 -translate-x-1/2',
    'top-right': 'top-6 right-6',
    'center-left': 'top-1/2 left-6 -translate-y-1/2',
    'center-center': 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
    'center-right': 'top-1/2 right-6 -translate-y-1/2',
    'bottom-left': 'bottom-6 left-6',
    'bottom-center': 'bottom-6 left-1/2 -translate-x-1/2',
    'bottom-right': 'bottom-6 right-6',
};

// Promo card. Content comes from the admin's Promo Cards page — bg is
// always applied (even with an image) as a fallback color while the photo
// loads, and a dark overlay keeps text readable over any image.
const SidePromo = ({ eyebrow, title, subtitle, price, bg, image, linkType, linkProduct, linkCategory, linkBrand, linkCustomUrl, buttonText, showOverlay, showButton, buttonPosition }) => {
    const linkTarget = getLinkHref({ linkType, linkProduct, linkCategory, linkBrand, linkCustomUrl });
    const positionClass = buttonPositionClasses[buttonPosition] || buttonPositionClasses['bottom-left'];

    return (
        <div className={`hidden lg:flex flex-col justify-between w-72 flex-shrink-0 rounded-lg p-6 text-white relative overflow-hidden ${bg}`}>
            {image && (
                <img draggable="false" src={image} alt={title} className="absolute inset-0 w-full h-full object-cover" />
            )}
            {image && showOverlay !== false && <div className="absolute inset-0 bg-black/30" />}
            <div className="relative">
                <p className="text-xs uppercase tracking-wide opacity-80 mb-2">{eyebrow}</p>
                <h3 className="text-xl font-semibold leading-snug mb-3">{title}</h3>
                {subtitle && <div className="text-sm opacity-90 mb-4" dangerouslySetInnerHTML={{ __html: subtitle }} />}
            </div>
            {price &&
                <div className="relative">
                    <p className="text-xs opacity-80">only from</p>
                    <p className="text-2xl font-bold">{price}</p>
                </div>
            }
            {linkTarget && buttonText && showButton !== false && (
                linkTarget.external ? (
                    <a
                        href={linkTarget.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`absolute ${positionClass} bg-white text-gray-800 text-sm font-medium px-4 py-2 rounded-full hover:bg-gray-100`}
                    >
                        {buttonText}
                    </a>
                ) : (
                    <Link
                        to={linkTarget.to}
                        className={`absolute ${positionClass} bg-white text-gray-800 text-sm font-medium px-4 py-2 rounded-full hover:bg-gray-100`}
                    >
                        {buttonText}
                    </Link>
                )
            )}
        </div>
    );
};

export default SidePromo;